import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ChevronLeft, ChevronRight, Target, Flame, Dumbbell, Trash2, Plus, Loader2,
} from "lucide-react";
import Navigation from "../components/Navigation";
import ProgressCards from "../components/nutrition/ProgressCards";
import WaterTracker from "../components/nutrition/WaterTracker";
import MealSection from "../components/nutrition/MealSection";
import FoodSearchDialog from "../components/nutrition/FoodSearchDialog";
import ExerciseDialog from "../components/nutrition/ExerciseDialog";
import GoalsDialog from "../components/nutrition/GoalsDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
const todayISO = () => new Date().toISOString().split("T")[0];

const shiftDate = (iso, days) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

const formatDate = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
};

export default function DailyLog({ user, onLogout }) {
  const [date, setDate] = useState(todayISO());
  const [summary, setSummary] = useState(null);
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [foodDialog, setFoodDialog] = useState({ open: false, meal: "breakfast" });
  const [exerciseDialog, setExerciseDialog] = useState(false);
  const [goalsDialog, setGoalsDialog] = useState(false);

  const load = useCallback(async (d = date) => {
    setLoading(true);
    try {
      const [sumRes, goalsRes] = await Promise.all([
        axios.get(`${API}/nutrition/daily-summary`, { params: { date: d }, headers: authHeaders() }),
        axios.get(`${API}/nutrition/goals`, { headers: authHeaders() }),
      ]);
      setSummary(sumRes.data);
      setGoals(goalsRes.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load daily log");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(date); }, [date, load]);

  const changeWater = async (delta) => {
    try {
      const res = await axios.post(`${API}/nutrition/water`, { date, delta_ml: delta }, { headers: authHeaders() });
      setSummary((s) => ({ ...s, water_ml: res.data.ml }));
    } catch { toast.error("Failed to update water"); }
  };

  const openFoodDialog = (meal) => setFoodDialog({ open: true, meal });

  const deleteFoodEntry = async (id) => {
    try {
      await axios.delete(`${API}/nutrition/entries/${id}`, { headers: authHeaders() });
      toast.success("Removed");
      load();
    } catch { toast.error("Failed to remove entry"); }
  };

  const deleteExercise = async (id) => {
    try {
      await axios.delete(`${API}/exercise/entries/${id}`, { headers: authHeaders() });
      toast.success("Removed");
      load();
    } catch { toast.error("Failed to remove"); }
  };

  const saveGoals = async (g) => {
    try {
      await axios.put(`${API}/nutrition/goals`, g, { headers: authHeaders() });
      toast.success("Goals saved");
      setGoalsDialog(false);
      load();
    } catch { toast.error("Failed to save goals"); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header + date navigator */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Nutrition &amp; Exercise</h1>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Log meals, macros, water, and training.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-white dark:bg-gray-900 rounded-full shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
              <button
                onClick={() => setDate(shiftDate(date, -1))}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                data-testid="date-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="px-3 py-1.5 text-sm font-semibold text-gray-800 dark:text-gray-200 min-w-[10rem] text-center" data-testid="current-date-label">
                {date === todayISO() ? "Today" : formatDate(date)}
              </div>
              <button
                onClick={() => setDate(shiftDate(date, 1))}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                data-testid="date-next-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-36 hidden md:block"
              data-testid="date-picker-input"
            />
            <Button
              variant="outline"
              onClick={() => setGoalsDialog(true)}
              title="Customize nutrition goals"
              className="shrink-0 px-3"
              data-testid="open-goals-btn"
            >
              <Target className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Nutrition goals</span>
            </Button>
          </div>
        </div>

        {loading || !summary ? (
          <div className="flex items-center justify-center py-24 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            <ProgressCards summary={summary} />

            <WaterTracker
              ml={summary.water_ml}
              goalMl={summary.goals?.water_ml}
              onDelta={changeWater}
            />

            {/* Meals */}
            <div className="space-y-3">
              {["breakfast", "lunch", "dinner", "snacks"].map((m) => (
                <MealSection
                  key={m}
                  meal={m}
                  data={summary.meals?.[m]}
                  onAdd={openFoodDialog}
                  onDelete={deleteFoodEntry}
                />
              ))}
            </div>

            {/* Exercise */}
            <Card className="border-0 shadow-md" data-testid="exercise-section">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-orange-50 text-orange-600">
                      <Dumbbell className="w-4 h-4" />
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white text-base">Exercise</h3>
                    <span className="text-xs tabular-nums text-orange-600 font-bold flex items-center gap-0.5">
                      <Flame className="w-3.5 h-3.5" /> {Math.round(summary.exercise?.calories_burned || 0)} kcal burned
                    </span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setExerciseDialog(true)}
                    className="h-8 px-2.5 bg-orange-500 hover:bg-orange-600"
                    data-testid="add-exercise-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add exercise
                  </Button>
                </div>

                {(summary.exercise?.entries || []).length === 0 ? (
                  <p className="text-xs text-gray-500 italic py-2 text-center">No activity logged yet today.</p>
                ) : (
                  <div className="space-y-1.5">
                    {summary.exercise.entries.map((e) => (
                      <div key={e.id} className="flex items-center justify-between gap-2 bg-orange-50/50 rounded-lg p-2 group">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{e.activity_name}</div>
                          <div className="text-[11px] text-gray-500 tabular-nums">
                            {Math.round(e.duration_min)} min · MET {e.met}
                            {e.notes && <span className="ml-1 text-gray-600">· {e.notes}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-sm font-bold text-orange-600 tabular-nums">{Math.round(e.calories_burned)} kcal</span>
                          <button
                            onClick={() => deleteExercise(e.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600"
                            data-testid={`delete-exercise-${e.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <FoodSearchDialog
        open={foodDialog.open}
        onOpenChange={(o) => setFoodDialog((s) => ({ ...s, open: o }))}
        meal={foodDialog.meal}
        date={date}
        onEntryAdded={() => load()}
      />

      <ExerciseDialog
        open={exerciseDialog}
        onOpenChange={setExerciseDialog}
        date={date}
        onAdded={() => load()}
        bodyWeightKg={goals?.body_weight_kg}
      />

      {goals && (
        <GoalsDialog
          open={goalsDialog}
          onOpenChange={setGoalsDialog}
          goals={goals}
          onSave={saveGoals}
        />
      )}
    </div>
  );
}
